/**
 * Service Contract Execution — status & enumerated types (Field Service · P11).
 * Ported from CONTRACT_DATA_MODELS.md, refreshed for the SCX PRD (#2972).
 */

/**
 * Contract lifecycle statuses. Single source of truth — the `ContractStatus`
 * type, the coverage-index status, and the zod enum all derive from this tuple
 * so they can never drift.
 */
export const CONTRACT_STATUSES = [
  'draft', // Being created / ingested
  'pending_review', // Awaiting internal review
  'approved', // Reviewed and approved, not yet active
  'active', // Currently in effect
  'expired', // Past end date
  'terminated', // Cancelled before end date
  'renewed', // Replaced by a renewal contract
] as const;

export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

export type ContractReviewStatus =
  | 'not_required'
  | 'pending'
  | 'in_review'
  | 'approved'
  | 'rejected'
  | 'revision_requested';

// IngestionSessionStatus moved to ./contractIngestionSession (the read-snapshot model).

/**
 * Cadence a contract service runs at. Distinct from the canonical
 * `ServiceOperation` taxonomy (which names the WORK); frequency names the RATE.
 */
export type ServiceFrequency =
  | 'monthly'
  | 'quarterly'
  | 'semi-annual'
  | 'annual'
  | 'as-needed'
  | 'custom';

export type EscalationType =
  | 'none'
  | 'fixed' // Fixed percentage
  | 'cpi' // Consumer Price Index
  | 'custom'; // Custom schedule

export type BillingFrequency =
  | 'monthly'
  | 'quarterly'
  | 'annually'
  | 'one_time'
  | 'per_service';

export type ContactRole =
  | 'primary'
  | 'billing'
  | 'site'
  | 'technical'
  | 'approver'
  | 'emergency'
  | 'other';

export type ScheduleConflictResolution =
  | 'keep_existing' // Keep old schedule, don't create new
  | 'replace' // Replace old schedule with contract schedule
  | 'merge' // Merge scope, keep earliest date
  | 'add_alongside'; // Keep both (may result in duplicates)

/**
 * Billing method a contract (or single contractService) uses — Phase F shape,
 * defined here so the contract data model is complete. One contract can mix
 * methods (PM covered under flatRecurring, load test billed perService).
 */
export type ContractBillingMethod =
  | 'flatRecurring' // Contract billed $X/period; visits covered (do-not-invoice-the-visit)
  | 'perService' // Each covered visit invoiced at the service's rate
  | 'coveredNoCharge' // In scope, no charge
  | 'timeAndMaterials'; // Out-of-scope / billable work

// `ScheduleAnchorMode` was removed with the Decision 16 reversal (D-N4): due dates are
// fixed from each schedule's own `firstServiceDate`, so there is no mode to choose.
// `fixedCalendar` is subsumed — a January first-service date recurs each January.
// See docs/contracts/OFFICEHUB_NATIVE_DESIGN.md section 4.2.
